import React, { useCallback, useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Button, CircularProgress, IconButton } from '@material-ui/core';

import toast from 'utils/toast.jsx';
import { getRequest, postRequest } from 'utils/api';
import { closeModal, openModal } from 'features/utilsSlice';
import { formatPrice } from 'utils/helpers';
import useModalOpen from 'hooks/useModalOpen';
import useCurrentUser from 'hooks/useCurrentUser';
import useModalData from 'hooks/useModalData';
import useWishlistCheckoutSuccess from 'hooks/useWishlistCheckoutSuccess';
import InfoIcon from 'public/icons/info.svg';
import CloseIcon from 'public/icons/close-main.svg';
import Subtotal from './Subtotal';
import WishInfo from './WishInfo';
import IconMainContainer from 'components/IconMainContainer';
import ActionCable from 'components/ActionCable/Cable';
import TransitionDialog from 'components/TransitionDialog';
import PaymentDialog from 'components/wishlist/PaymentDialog';
import PaymentOptions from 'components/cart/PaymentOptions';
import styles from 'styles/components/cart/Cart.module.scss';

const WishCheckoutDialog = ({ profile }) => {
  const [paymentOption, setPaymentOption] = useState('card');
  const [loading, setLoading] = useState(false);
  const [priceInfo, setPriceInfo] = useState({});
  const [userInfo, setUserInfo] = useState({ name: '', email: '' });
  const [wishInfo, setWishInfo] = useState({ amount: 0, message: '' });

  const isLoggedIn = useSelector(state => state.auth.loggedIn);
  const user = useCurrentUser();
  const open = useModalOpen('Wish Checkout');

  const { wish } = useModalData('Wish Checkout');
  const dispatch = useDispatch();
  const buttonText = 'Checkout';

  const handleCheckoutSuccess = useWishlistCheckoutSuccess();

  const handleClose = () => {
    dispatch(closeModal('Wish Checkout'));
    setUserInfo({
      name: user?.name,
      email: user?.email || user?.contact_email,
    });

    setWishInfo({ amount: 0, message: '' });

    if (wish.kind === 'crowdfunding')
      setWishInfo(prevState => ({ ...prevState, amount: wish.price }));
  };

  const fetchPricingInfo = useCallback(() => {
    if (!wish?.id || (wish.kind === 'crowdfunding' && !wishInfo.amount)) return;

    getRequest({
      endpoint: `wishlist_items/${wish?.id}/pricing`,
      query: { amount: wishInfo.amount },
    })
      .then(data => setPriceInfo(data))
      .catch(() => {});
  }, [wishInfo.amount, wish?.id, wish?.kind]);

  const handleCheckout = () => {
    if (paymentOption !== 'wallet') {
      dispatch(openModal({ name: 'Payment', data: { userInfo, wishInfo, priceInfo } }));
      return;
    }

    setLoading(true);

    postRequest({
      endpoint: `wishlist_items/${wish.id}/checkout?async=true`,
      data: { payment_method: 'wallet' },
      successMessage: '',
      errorMessage: 'There was an error during the checkout. Please try again or contact support.',
    });
  };

  useEffect(() => {
    fetchPricingInfo();
  }, [fetchPricingInfo]);

  useEffect(() => {
    if (!isLoggedIn) return;

    setUserInfo({
      name: user?.name,
      email: user?.email,
    });
  }, [user?.name, user?.email, isLoggedIn]);

  useEffect(() => {
    if (!wish || !wish.kind === 'crowdfunding') return;

    setWishInfo(prevState => ({ ...prevState, amount: wish.price }));
  }, [wish]);

  if (!wish) return null;

  const amountValid =
    (wish.kind === 'crowdfunding' && wishInfo.amount) || wish.kind === 'single_wish';
  const disabled = !amountValid || !userInfo.name || !userInfo.email;

  return (
    <>
      <TransitionDialog
        open={open}
        handleClose={handleClose}
        noCloseIcon
        direction="up"
        styles={styles}
      >
        {paymentOption === 'wallet' && (
          <ActionCable
            channel="CheckoutChannel"
            onReceived={({ type, payload }) => {
              if (type === 'success') {
                handleCheckoutSuccess();
                setLoading(false);
              } else {
                setLoading(false);
                toast(
                  'error',
                  payload?.errors?.[0] ||
                    'There was an error during the checkout. Please try again or contact support.',
                  {
                    icon: (
                      <IconMainContainer
                        src="/icons/notifications/times.svg"
                        alt="Times"
                        errorStyle
                      />
                    ),
                  }
                );
              }
            }}
          />
        )}

        <div className={styles.cart}>
          <div className={styles.header}>
            <IconButton onClick={handleClose}>
              <CloseIcon />
            </IconButton>
            {wish.name}
          </div>

          <div className={styles.content}>
            <div className={styles.info}>
              <div className={styles.title}>
                <h3>PEACE OF MIND</h3>
                <InfoIcon width={16} height={16} alt="Info" />
              </div>

              <p>Your payment is processed securely.</p>
            </div>

            <WishInfo
              wish={wish}
              wishInfo={wishInfo}
              setWishInfo={setWishInfo}
              userInfo={userInfo}
              setUserInfo={setUserInfo}
              profile={profile}
            />
          </div>

          <Subtotal priceInfo={priceInfo} styles={styles} />

          <PaymentOptions
            styles={styles}
            paymentOption={paymentOption}
            setPaymentOption={setPaymentOption}
            totalPrice={priceInfo.checkout_price ? priceInfo.checkout_price : 'N/A'}
          />

          <div className={styles.footer}>
            <span className={styles.price}>
              {priceInfo.checkout_price ? formatPrice(priceInfo.checkout_price) : 'N/A'}
            </span>

            <Button
              variant="contained"
              color="primary"
              disabled={disabled}
              onClick={handleCheckout}
            >
              {buttonText} {loading && <CircularProgress size="2rem" style={{ marginLeft: 10 }} />}
            </Button>

            <PaymentDialog paymentOption={paymentOption} userInfo={userInfo} wishInfo={wishInfo} />
          </div>
        </div>
      </TransitionDialog>
    </>
  );
};

export default WishCheckoutDialog;
